import { Button, Dropdown, Input, Modal } from "@amzn/alchemy-components-react";
import { useTranslation } from "react-i18next";
import { ModalCloseSource, TenantID, UserType } from "src/common/enums";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "src/logger";
import { isEmptyString } from "src/common/functions";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_USER_INFO_BY_ALIAS_QUERY, CONFIRM_BULK_ADD_USER_BY_ALIAS_MUTATION } from "src/common/gql-operations";
import { NETWORK_ONLY } from "src/common/constants";
import { InlineError } from "src/components/inline-error";
import { FallbackSpinner } from "src/components/fallback-spinner";
import { resolveI18nMessage, useI18nMessage } from "src/common/i18n-message";
import initialMetricsPublisher from "src/metrics";
import * as KatalMetrics from "@amzn/katal-metrics";
import {
    ConfirmAddUserByAliasEntry,
    ConfirmBulkAddUserByAliasData,
    ConfirmBulkAddUserByAliasVariables,
    ResolvedPerson
} from "src/models/bulk-add-user-by-alias";
import { ValidationError } from "src/models/validation-error";

const cloudWatchDimensions = [
    new KatalMetrics.Metric.String('modal', 'add-user'),
];

const additionalMetricsContext = new KatalMetrics.Context({ cloudWatchDimensions });
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);

interface AddUserModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    scope: string;
    onSuccess?: (message: string) => void;
}

interface FormState {
    systemAccessCredential: string;
    selectedUserType: string;
    email: string;
}

type ModalStep = 'form' | 'confirmation';

const requiresEmail = (userType: string): boolean => {
    return userType === UserType.USER_ADMIN_PRIMARY ||
        userType === UserType.USER_ADMIN_SECONDARY;
};

export const AddUserModal = (props: AddUserModalProps) => {
    const { t } = useTranslation();

    const [formState, setFormState] = useState<FormState>({
        systemAccessCredential: '',
        selectedUserType: '',
        email: ''
    });

    const isValid = !isEmptyString(formState.systemAccessCredential)
        && !isEmptyString(formState.selectedUserType)
        && (!requiresEmail(formState.selectedUserType) || !isEmptyString(formState.email));

    // Inline error stored as a deferred message so it re-translates on language
    // change instead of freezing in the language active when it was set.
    const {
        message: errorMessage,
        setKey: setErrorKey,
        setValidation: setValidationError,
        setRaw: setRawError,
        clear: clearError
    } = useI18nMessage();
    const [userInfo, setUserInfo] = useState<ResolvedPerson | null>(null);
    const [modalStep, setModalStep] = useState<ModalStep>('form');

    const [getUserInfoByAlias, { loading: getUserInfoLoading }] = useLazyQuery(GET_USER_INFO_BY_ALIAS_QUERY, {
        fetchPolicy: NETWORK_ONLY,
        onCompleted: (data) => {
            logger.info("getUserInfoByAlias succeeded", data);
            graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.SUCCESS', 1);

            const persons = data?.getUserInfoByAlias?.persons;
            // The API returns an array but we query by a single alias, so we expect exactly one result.
            // The bulk add user feature will handle multiple persons in the response.
            if (persons && persons.length > 0) {
                const person = persons[0];
                if (!person.isActive) {
                    setErrorKey('user-is-not-active', { alias: person.alias });
                    return;
                }
                setUserInfo(person);
                setModalStep('confirmation');
                clearError();
            } else {
                setErrorKey('no-user-found-with-alias', { alias: submittedAliasRef.current });
            }
        },
        onError: error => {
            logger.error("Call to getUserInfoByAlias failed!", error);
            graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.ERROR', 1);
            setRawError(error.message);
        }
    });

    // Ref is needed because Apollo's onCompleted/onError callbacks capture stale
    // closure state. A ref lets the callback read the latest submitted alias
    // without depending on a re-render cycle.
    const submittedAliasRef = useRef<string>('');
    const pendingSuccessMessageRef = useRef<string | null>(null);
    // Set synchronously in handleConfirm so a rapid double-click can't fire a
    // second mutation before React re-renders with confirmLoading=true (which
    // is what disables the button). Cleared when the mutation settles.
    const confirmInFlightRef = useRef<boolean>(false);

    const [confirmBulkAddUserByAlias, { loading: confirmLoading }] =
        useMutation<ConfirmBulkAddUserByAliasData, ConfirmBulkAddUserByAliasVariables>(
            CONFIRM_BULK_ADD_USER_BY_ALIAS_MUTATION, {
        onCompleted: (data) => {
            confirmInFlightRef.current = false;
            const result = data?.confirmBulkAddUserByAlias;
            const rowErrors = (result?.validationResults ?? []).reduce<ValidationError[]>(
                (acc, r) => acc.concat(r.errors ?? []), []);
            const commonErrors = result?.commonValidationErrors ?? [];

            // >= 1 matches the shared bulk API contract; for this bulk-of-1 call successCount is always 0 or 1.
            if ((result?.successCount ?? 0) >= 1 && rowErrors.length === 0 && commonErrors.length === 0) {
                logger.info("confirmBulkAddUserByAlias succeeded");
                // Metric names retained as 'add-principal-scope.*' for dashboard continuity across the migration.
                graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.SUCCESS', 1);
                const msg = pendingSuccessMessageRef.current;
                pendingSuccessMessageRef.current = null;
                handleModalClose(ModalCloseSource.SUCCESS);
                if (msg) props.onSuccess?.(msg);
                return;
            }

            // Validation rejection: surface the first available structured error and stay on the step.
            logger.warn("confirmBulkAddUserByAlias returned validation errors", { rowErrors, commonErrors });
            graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.VALIDATION_ERROR', 1);
            pendingSuccessMessageRef.current = null;
            const firstError = rowErrors[0] ?? commonErrors[0];
            if (firstError) {
                setValidationError(firstError);
            } else {
                setErrorKey('user-could-not-be-added');
            }
        },
        onError: error => {
            confirmInFlightRef.current = false;
            logger.error("Call to confirmBulkAddUserByAlias failed!", error);
            graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.ERROR', 1);
            pendingSuccessMessageRef.current = null;
            setRawError(error.message);
        }
    });

    // Resolve the deferred error to a string. Called during render so a language
    // change re-resolves the message instead of showing a stale translation.
    const renderError = (): string | null => resolveI18nMessage(t, errorMessage);

    const resetForm = useCallback(() => {
        setFormState({
            systemAccessCredential: '',
            selectedUserType: '',
            email: ''
        });
        setUserInfo(null);
        setModalStep('form');
        // Clear the in-flight guard so a confirm that was still pending when the
        // modal closed can't leak `true` into the next open and silently block submit.
        confirmInFlightRef.current = false;
        clearError();
    }, []);

    useEffect(() => {
        if (!props.open) {
            resetForm();
        }
    }, [props.open, resetForm]);

    const handleModalClose = (source: ModalCloseSource) => {
        logger.info(`Add User Modal closing from: ${source}`);
        resetForm();
        props.setOpen(false);
    };

    const handleSystemAccessCredentialInput = (value: string) => {
        setFormState({
            ...formState,
            systemAccessCredential: value
        });
    };

    const handleUserTypeSelection = (userType: string) => {
        logger.info(`Selected User Type: ${userType}`);
        setFormState({
            ...formState,
            selectedUserType: userType,
            email: requiresEmail(userType) ? formState.email : ''
        });
    };

    const handleEmailInput = (value: string) => {
        setFormState({
            ...formState,
            email: value
        });
    };

    const handleSubmit = () => {
        clearError();

        if (!isValid) {
            setErrorKey('please-fill-in-all-fields');
            return;
        }

        const trimmedAlias = formState.systemAccessCredential.trim();
        submittedAliasRef.current = trimmedAlias;

        logger.info(`Submitting with system access credential: ${trimmedAlias}, UserType: ${formState.selectedUserType}, Scope: ${props.scope}`);

        graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.INVOCATION', 1);
        getUserInfoByAlias({
            variables: {
                getUserInfoByAliasInput: {
                    tenantId: TenantID.AFTX,
                    aliases: [trimmedAlias]
                }
            }
        });
    };

    const handleConfirm = () => {
        // Guard against a double-click firing a second mutation before
        // confirmLoading re-renders the button into its disabled state.
        if (confirmInFlightRef.current) {
            return;
        }

        clearError();

        if (!userInfo) {
            setErrorKey('user-info-not-available');
            return;
        }

        logger.info(`Confirming add user: ${userInfo.alias} to scope ${props.scope}`);
        pendingSuccessMessageRef.current = t('user-has-been-added-to-scope', { scope: props.scope, role: formState.selectedUserType });

        graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.INVOCATION', 1);
        confirmInFlightRef.current = true;
        const entry: ConfirmAddUserByAliasEntry = {
            principalArn: userInfo.principalArn,
            userType: formState.selectedUserType as UserType,
            rowNumber: 1,
            ...(requiresEmail(formState.selectedUserType) && { email: formState.email.trim() })
        };
        confirmBulkAddUserByAlias({
            variables: {
                confirmBulkAddUserByAliasInput: {
                    scope: props.scope,
                    tenantId: TenantID.AFTX,
                    users: [entry]
                }
            }
        });
    };

    const handleBack = () => {
        setModalStep('form');
        setUserInfo(null);
        clearError();
    };

    const userTypeOptions = [
        { label: t('select-user-type'), value: '' },
        { label: UserType.ASSOCIATE, value: UserType.ASSOCIATE },
        { label: UserType.USER_ADMIN_PRIMARY, value: UserType.USER_ADMIN_PRIMARY },
        { label: UserType.USER_ADMIN_SECONDARY, value: UserType.USER_ADMIN_SECONDARY },
        { label: UserType.ABBM_SECONDARY, value: UserType.ABBM_SECONDARY }
    ];

    const renderFormStep = () => {
        const error = renderError();
        return (
        <>
            {error && <InlineError message={error} />}

            <div className="row mb-3">
                <div className="col">
                    <Input
                        id="system-access-credential-input"
                        label={t('system-access-credentials')}
                        type="text"
                        value={formState.systemAccessCredential}
                        onInput={e => handleSystemAccessCredentialInput(e.target.value)}
                    />
                </div>
            </div>

            <div className="row mb-3">
                <div className="col">
                    <Dropdown
                        id="user-type-select"
                        label={t('select-user-type')}
                        options={userTypeOptions}
                        onChange={e => handleUserTypeSelection(e.target.value)}
                        value={formState.selectedUserType}
                    />
                </div>
            </div>

            {requiresEmail(formState.selectedUserType) && (
                <div className="row mb-3">
                    <div className="col">
                        <Input
                            id="email-input"
                            label={t('email')}
                            type="email"
                            value={formState.email}
                            onInput={e => handleEmailInput(e.target.value)}
                        />
                    </div>
                </div>
            )}

            <div className="row d-flex justify-content-center modal-button-row">
                <Button
                    id="add-user-modal-cancel-button"
                    size="lg"
                    label={t('cancel')}
                    onClick={() => handleModalClose(ModalCloseSource.CANCEL)}
                    className="mx-1"
                />
                <Button
                    id="add-user-modal-submit-button"
                    size="lg"
                    label={t('submit')}
                    variant="action"
                    disabled={!isValid || getUserInfoLoading}
                    onClick={handleSubmit}
                    className="mx-1"
                />
            </div>
        </>
    );
    };

    const renderConfirmationStep = () => {
        const error = renderError();
        return (
        <>
            {error && <InlineError message={error} />}

            <div className="row mb-3">
                <div className="col">
                    <p>{t('please-confirm-add-user-to-scope', { scope: props.scope })}</p>
                </div>
            </div>

            <div className="row mb-3">
                <div className="col">
                    <table className="alchemy-table bordered" role="table" aria-label={t('please-confirm-add-user-to-scope', { scope: props.scope })} style={{ width: '100%', tableLayout: 'auto' }}>
                        <thead>
                        <tr>
                            <th>{t('system-access-credentials')}</th>
                            <th>{t('name')}</th>
                            <th>{t('job-type')}</th>
                            <th>{t('user-type')}</th>
                            {requiresEmail(formState.selectedUserType) && <th>{t('email')}</th>}
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td>{userInfo?.alias}</td>
                            <td>{userInfo?.firstName} {userInfo?.lastName}</td>
                            <td>{userInfo?.jobType}</td>
                            <td>{formState.selectedUserType}</td>
                            {requiresEmail(formState.selectedUserType) && <td>{formState.email}</td>}
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="row d-flex justify-content-center modal-button-row">
                <Button
                    id="add-user-modal-back-button"
                    size="lg"
                    label={t('back')}
                    onClick={handleBack}
                    className="mx-1"
                />
                <Button
                    id="add-user-modal-confirm-button"
                    size="lg"
                    label={t('confirm')}
                    variant="action"
                    disabled={confirmLoading}
                    onClick={handleConfirm}
                    className="mx-1"
                />
            </div>
        </>
    );
    };

    const isLoading = getUserInfoLoading || confirmLoading;

    return (
        <Modal
            id="add-user-modal"
            className="mb-1 add-user-modal"
            header={t('add-user-to-scope', { scope: props.scope })}
            open={props.open}
            size="x-large"
            onClose={(e: React.MouseEvent) => {
                if (e.target === e.currentTarget) {
                    handleModalClose(ModalCloseSource.OVERLAY);
                } else if ((e.target as HTMLElement).closest('.alchemy-modal-close-button')) {
                    handleModalClose(ModalCloseSource.CROSS);
                }
            }}
        >
            {isLoading && <FallbackSpinner />}
            <div className="container-fluid" onClick={(e) => e.stopPropagation()} style={{ paddingTop: '2rem', paddingLeft: '4rem', paddingRight: '4rem' }}>
                {modalStep === 'form' && renderFormStep()}
                {modalStep === 'confirmation' && renderConfirmationStep()}
            </div>
        </Modal>
    );
};
